import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../core/lib/api";
import { PageHeader } from "../../core/components/ui/page-header";
import { Card } from "../../core/components/ui/card";
import { Button } from "../../core/components/ui/button";
import { Input } from "../../core/components/ui/input";

interface Summary {
  report: {
    importedAt: string;
    keptAccount: string;
    counts: Record<string, number>;
    warnings: string[];
  } | null;
  datasets: { dataset: string; count: number }[];
  files: { path: string; mime: string; bytes: number }[];
}
interface Data {
  columns: string[];
  rows: Record<string, string>[];
  total: number;
}
const names: Record<string, string> = {
  entreprise: "Entreprise",
  categories: "Catégories",
  services: "Services d’origine",
  tarifs_services: "Tarifs et variantes",
  clients: "Clients et consentements",
  client_vehicules_releve: "Véhicules relevés",
  employes: "Équipe et rôles",
  horaires_hebdomadaires: "Horaires",
  parametres: "Préférences",
  couleurs_agenda: "Couleurs d’agenda",
  evenements_agenda: "Événements et récurrences",
  absences_controle: "Contrôles d’absences",
  taxes: "Taxes",
  messages: "Messages",
  rappels_services: "Rappels par service",
  parrainage: "Parrainage",
  urls: "Liens",
  formulaires: "Formulaires",
  formulaires_champs: "Champs des formulaires",
  formulaires_options: "Choix des formulaires",
  integrations: "Intégrations",
  associations_services: "Services complémentaires",
  procedures: "Procédures",
  guides: "Documents liés",
  exemples_jobs: "Rendez-vous d’exemple",
  exemples_jobs_lignes: "Prestations du rendez-vous",
  agenda_jobs: "Agenda complet et devis Urable",
  agenda_clients: "Clients de l’export agenda",
  couverture: "Étendue du relevé",
  correspondance_crm: "Correspondances",
  schema_csv: "Dictionnaire des colonnes",
  manifest: "Index des fichiers",
};
const label = (key: string) => key.replaceAll("_", " ");
const value = (text: string) => (text === "true" ? "Oui" : text === "false" ? "Non" : text || "—");

export function UrablePage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("dataset") ?? "parametres",
    q = params.get("q") ?? "",
    offset = Number(params.get("offset")) || 0;
  const [summary, setSummary] = useState<Summary | null>(null),
    [data, setData] = useState<Data | null>(null),
    [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    api<Summary>("/api/urable/summary")
      .then((s) => {
        if (active) setSummary(s);
      })
      .catch(() => {
        if (active) setError("Impossible de charger les données importées.");
      });
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    let active = true;
    setData(null);
    if (!summary?.report) return;
    setError("");
    api<Data>(
      `/api/urable/datasets/${encodeURIComponent(selected)}?${new URLSearchParams({ q, offset: String(offset) })}`,
    )
      .then((d) => {
        if (active) setData(d);
      })
      .catch(() => {
        if (active) setError("Impossible de charger cet ensemble de données.");
      });
    return () => {
      active = false;
    };
  }, [summary, selected, q, offset]);
  const change = (patch: Record<string, string>) =>
    setParams({ ...Object.fromEntries(params), ...patch });
  return (
    <div className="page urable-data">
      <PageHeader
        title="Données Urable"
        subtitle="Les données d’origine, leurs documents et les points restant à préciser."
      />
      {error ? <p role="alert">{error}</p> : null}
      {!summary ? (
        <p>Chargement…</p>
      ) : !summary.report ? (
        <p>Aucun import Urable n’a encore été appliqué à cette plateforme.</p>
      ) : (
        <>
          <Card
            title={`Import du ${new Date(summary.report.importedAt).toLocaleDateString("fr-CA")}`}
          >
            <p>
              Les clients, le catalogue, l’équipe, les procédures, les modèles de messages et le
              rendez-vous sont intégrés dans leurs écrans habituels.
            </p>
            <nav className="urable-data__links" aria-label="Données intégrées">
              <Link to="/clients">Clients</Link>
              <Link to="/catalogue">Catalogue</Link>
              <Link to="/equipe">Équipe</Link>
              <Link to="/agenda">Agenda</Link>
              <Link to="/site">Réservation en ligne</Link>
            </nav>
            <details>
              <summary>Points à préciser</summary>
              <ul>
                {(summary.report.warnings ?? []).map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </details>
          </Card>
          <div className="urable-data__layout">
            <nav className="urable-data__sections" aria-label="Ensembles Urable">
              {summary.datasets.map((d) => (
                <button
                  key={d.dataset}
                  aria-current={selected === d.dataset ? "page" : undefined}
                  onClick={() => change({ dataset: d.dataset, q: "", offset: "0" })}
                >
                  {names[d.dataset] ?? label(d.dataset)} <span>{d.count}</span>
                </button>
              ))}
            </nav>
            <section className="urable-data__content" aria-label={names[selected] ?? selected}>
              <div className="urable-data__toolbar">
                <h2>{names[selected] ?? label(selected)}</h2>
                <a href={`/api/urable/file?path=${encodeURIComponent(`csv/${selected}.csv`)}`}>
                  Télécharger le CSV
                </a>
              </div>
              <Input
                aria-label="Rechercher dans ces données"
                placeholder="Rechercher un nom, un service, une valeur…"
                value={q}
                onChange={(e) => change({ q: e.target.value, offset: "0" })}
              />
              {!data ? (
                <p>Chargement…</p>
              ) : (
                <>
                  <p>
                    {data.total} résultat{data.total > 1 ? "s" : ""}
                  </p>
                  <div className="urable-data__table">
                    <table>
                      <thead>
                        <tr>
                          {data.columns.map((c) => (
                            <th key={c}>{label(c)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.rows.map((r, i) => (
                          <tr key={offset + i}>
                            {data.columns.map((c) => (
                              <td key={c}>{value(r[c] ?? "")}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="urable-data__toolbar">
                    <Button
                      variant="ghost"
                      disabled={offset === 0}
                      onClick={() => change({ offset: String(Math.max(0, offset - 100)) })}
                    >
                      Précédent
                    </Button>
                    <span>
                      {Math.min(offset + 1, data.total)}–{Math.min(offset + 100, data.total)} /{" "}
                      {data.total}
                    </span>
                    <Button
                      variant="ghost"
                      disabled={offset + 100 >= data.total}
                      onClick={() => change({ offset: String(offset + 100) })}
                    >
                      Suivant
                    </Button>
                  </div>
                </>
              )}
            </section>
          </div>
          <Card title="Documents et exports originaux">
            <details>
              <summary>{summary.files.length} fichiers conservés</summary>
              <ul>
                {summary.files.map((f) => (
                  <li key={f.path}>
                    <a href={`/api/urable/file?path=${encodeURIComponent(f.path)}`}>{f.path}</a> ·{" "}
                    {Math.ceil(f.bytes / 1024)} ko
                  </li>
                ))}
              </ul>
            </details>
          </Card>
        </>
      )}
    </div>
  );
}
